import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DetailPanel } from "./DetailPanel";
import {
  Monitor, Wifi, WifiOff, Package, AlertTriangle, ShoppingBag, Clock, Lock,
  Boxes, Plus, Minus, ExternalLink, Activity as ActivityIcon, TrendingUp, Trash2, LayoutGrid, Rocket, PowerOff, CheckCircle2, Circle, Bell, Users, UserCog, ArrowRight, HeartPulse, Laptop, Tablet, Pencil,
} from "lucide-react";
import PlanogramEditor from "./PlanogramEditor";
import { KioskHealth } from "./KioskHealth";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

function timeAgo(iso) {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function PeriodCard({ label, data, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-indigo-200 bg-indigo-50/60 dark:border-indigo-900 dark:bg-indigo-950/30" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-white">{money(data?.revenue)}</p>
      <p className="text-xs text-slate-400">{data?.count || 0} sale(s)</p>
    </div>
  );
}

function Fact({ icon: Icon, label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-900 dark:text-white", rose: "text-rose-600", emerald: "text-emerald-600" };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className={`mt-1 text-xl font-bold ${tones[tone]}`}>{value}</p>
    </div>
  );
}

const ACT_ICON = { sale: ShoppingBag, stock: Boxes, pin: Lock };

function CheckRow({ ok, label, detail, required }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600 shrink-0" />}
      <p className={`text-sm font-medium flex-1 min-w-0 ${ok ? "text-slate-900 dark:text-white" : "text-slate-500"}`}>
        {label}{required && !ok && <span className="text-rose-500"> *</span>}
      </p>
      <span className="text-xs text-slate-400 text-right">{detail}</span>
    </div>
  );
}

export function KioskPanel({ kioskId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [adjusting, setAdjusting] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [addPid, setAddPid] = useState("");
  const [addQuery, setAddQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [activatePrompt, setActivatePrompt] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [renaming, setRenaming] = useState(null);

  const saveName = async () => {
    const name = (renaming || "").trim();
    if (!name) { toast.error("Kiosk name can't be empty"); return; }
    try {
      await api.put(`/kiosks/${kioskId}`, { name });
      setData((d) => ({ ...d, name }));
      setRenaming(null);
      toast.success("Kiosk renamed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename kiosk"); }
  };

  const load = useCallback(() => {
    if (!kioskId) { setData(null); return; }
    setLoading(true);
    api.get(`/kiosks/${kioskId}/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [kioskId]);

  useEffect(() => { setTab("overview"); setConfirming(false); setTyped(""); setAddPid(""); setAddQuery(""); setAddOpen(false); setActivatePrompt(false); setPin(""); setPinErr(""); setRenaming(null); load(); }, [kioskId, load]);
  useEffect(() => { api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {}); }, []);

  const online = data?.status === "online";

  const toggleNotifEvent = async (key, val) => {
    const next = { ...(data.notif_events || {}), [key]: val };
    setData((d) => ({ ...d, notif_events: next }));
    try {
      await api.put(`/kiosks/${kioskId}`, { notif_events: next });
    } catch {
      toast.error("Could not update kiosk notifications");
      load();
    }
  };

  const adjust = async (p, delta) => {
    const next = Math.max(0, (p.stock_qty || 0) + delta);
    if (next === p.stock_qty) return;
    setAdjusting(p.product_id);
    try {
      await api.post("/stock/adjust", { kiosk_id: data.id, product_id: p.product_id, new_qty: next, reason: "manual" });
      setData((d) => ({ ...d, products: d.products.map((x) => (x.product_id === p.product_id ? { ...x, stock_qty: next, low: x.par_level != null ? next <= x.par_level : x.low } : x)) }));
    } catch {
      toast.error("Could not adjust stock");
    } finally {
      setAdjusting(null);
    }
  };

  const addProduct = async () => {
    if (!addPid) return;
    setMutating(true);
    try {
      await api.post("/kiosk-products", { kiosk_id: data.id, product_id: addPid, stock_qty: 0, par_level: 5, active: true });
      toast.success("Product added to kiosk");
      setAddPid("");
      setAddQuery("");
      setAddOpen(false);
      load();
    } catch { toast.error("Could not add product"); }
    finally { setMutating(false); }
  };

  const removeProduct = async (p) => {
    setMutating(true);
    try {
      await api.delete(`/kiosks/${data.id}/products/${p.product_id}`);
      toast.success("Product removed from kiosk");
      setData((d) => ({ ...d, products: d.products.filter((x) => x.product_id !== p.product_id), product_count: Math.max(0, (d.product_count || 1) - 1) }));
    } catch { toast.error("Could not remove product"); }
    finally { setMutating(false); }
  };

  const doDelete = async () => {    setDeleting(true);
    try {
      await api.delete(`/kiosks/${data.id}`);
      toast.success("Kiosk retired");
      setConfirming(false);
      setTyped("");
      onClose?.();
    } catch {
      toast.error("Could not retire kiosk");
    } finally {
      setDeleting(false);
    }
  };

  const reactivate = async () => {
    setToggling(true);
    try {
      await api.post(`/kiosks/${data.id}/activate`);
      setData((d) => ({ ...d, activated: true }));
      toast.success("Kiosk is live again");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-activate kiosk");
    } finally {
      setToggling(false);
    }
  };

  const deactivate = async () => {
    setConfirmDeactivate(false);
    setToggling(true);
    try {
      await api.post(`/kiosks/${data.id}/deactivate`);
      setData((d) => ({ ...d, activated: false }));
      toast.success("Kiosk deactivated — back to draft", { action: { label: "Undo", onClick: reactivate } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update kiosk");
    } finally {
      setToggling(false);
    }
  };

  const confirmActivate = async () => {
    if ((data.product_count || 0) === 0) { toast.error("Add at least one product first"); return; }
    if (pin && !/^\d{4,6}$/.test(pin)) { setPinErr("PIN must be 4–6 digits"); return; }
    setToggling(true);
    try {
      if (pin && !data.pin_set) await api.put(`/kiosks/${data.id}/pin`, { pin });
      await api.post(`/kiosks/${data.id}/activate`);
      toast.success("Kiosk activated — it's now live");
      setActivatePrompt(false);
      setPin("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate kiosk");
    } finally {
      setToggling(false);
    }
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: ActivityIcon },
    { key: "transactions", label: "Transactions", icon: ShoppingBag },
    { key: "products", label: "Products", icon: Package },
    { key: "planogram", label: "Planogram", icon: LayoutGrid },
    { key: "health", label: "Health", icon: HeartPulse },
  ];

  const actions = data?.code ? (
    data.activated ? (
      <a
        href={`/kiosk/${data.code}`}
        target="_blank"
        rel="noreferrer"
        data-testid="kiosk-panel-launch"
        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 h-9 text-sm font-semibold transition-colors"
      >
        <ExternalLink className="h-4 w-4" /> Launch Kiosk
      </a>
    ) : (
      <a
        href={`/kiosk/${data.code}`}
        target="_blank"
        rel="noreferrer"
        data-testid="kiosk-panel-preview-draft"
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 px-3 h-9 text-sm font-semibold transition-colors"
      >
        <ExternalLink className="h-4 w-4" /> Preview draft
      </a>
    )
  ) : null;

  return (
    <DetailPanel
      open={!!kioskId}
      onClose={onClose}
      eyebrow={data?.code || "Kiosk"}
      title={data?.name || "Kiosk"}
      icon={Monitor}
      actions={actions}
      width="w-full sm:w-[640px] lg:w-[60%]"
      testid="kiosk-panel"
    >
      {loading && <p className="text-sm text-slate-400" data-testid="kiosk-panel-loading">Loading kiosk…</p>}
      {!loading && !data && <p className="text-sm text-slate-400">Could not load this kiosk.</p>}
      {!loading && data && (
        <div className="space-y-5">
          {/* Header status row */}
          <div className="flex flex-wrap items-center gap-3">
            <span
              data-testid="kiosk-panel-lifecycle"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${data.activated ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
            >
              {data.activated ? <Rocket className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
              {data.activated ? "Live" : "Draft"}
            </span>
            <span
              data-testid="kiosk-panel-status"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${online ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
            >
              {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {online ? "Online" : "Offline"}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <Clock className="h-3.5 w-3.5" /> Last seen {timeAgo(data.last_seen)}
            </span>
            {data.pin_set && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 px-3 py-1 text-xs font-semibold"><Lock className="h-3 w-3" /> PIN protected</span>}
          </div>
          <div className="-mt-2 flex items-center gap-2 flex-wrap">
            <p className="text-sm text-slate-500 dark:text-slate-400 min-w-0">
              {[data.org_name, data.building_name, data.location_name].filter(Boolean).join("  ·  ") || "—"}
            </p>
            {renaming === null && (
              <button data-testid="kiosk-panel-rename-open" onClick={() => setRenaming(data.name || "")} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline">
                <Pencil className="h-3.5 w-3.5" /> Rename kiosk
              </button>
            )}
          </div>
          {renaming !== null && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="kiosk-panel-rename-box">
              <input
                data-testid="kiosk-panel-rename-input"
                autoFocus
                value={renaming}
                maxLength={60}
                onChange={(e) => setRenaming(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setRenaming(null); }}
                className="h-9 flex-1 min-w-[180px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-sm"
              />
              <button data-testid="kiosk-panel-rename-save" onClick={saveName} className="h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 text-sm font-semibold">Save</button>
              <button data-testid="kiosk-panel-rename-cancel" onClick={() => setRenaming(null)} className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 px-3 text-sm">Cancel</button>
            </div>
          )}

          {/* Lifecycle control */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{data.activated ? "This kiosk is live" : "This kiosk is in draft"}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {data.activated
                    ? "The live tablet URL is available above. Deactivating hides it and takes the kiosk offline for customers."
                    : "Work through the checklist, then activate to generate the live tablet URL."}
                </p>
              </div>
              {data.activated ? (
                <button
                  data-testid="kiosk-panel-activate-toggle"
                  onClick={() => setConfirmDeactivate(true)}
                  disabled={toggling}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-sm font-semibold transition-colors disabled:opacity-40 border border-rose-200 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                ><PowerOff className="h-4 w-4" /> Deactivate</button>
              ) : !activatePrompt ? (
                <button
                  data-testid="kiosk-panel-activate-toggle"
                  onClick={() => setActivatePrompt(true)}
                  disabled={(data.product_count || 0) === 0}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700 text-white"
                ><Rocket className="h-4 w-4" /> Activate kiosk</button>
              ) : null}
            </div>

            {!data.activated && (
              <div className="rounded-lg border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800" data-testid="kiosk-activation-checklist">
                <CheckRow ok={(data.product_count || 0) > 0} required label="Products added" detail={`${data.product_count || 0} product(s)`} />
                <CheckRow ok={!!data.has_planogram} label="Planogram set" detail={data.has_planogram ? "Layout saved" : "Recommended"} />
                <CheckRow ok={!!data.pin_set || !!pin} label="Pairing PIN" detail={data.pin_set ? "PIN protected" : (pin ? "Set on activate" : "Recommended")} />
              </div>
            )}

            {activatePrompt && !data.activated && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 space-y-3" data-testid="kiosk-activate-prompt">
                {!data.pin_set && (
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Set a pairing PIN (recommended)</label>
                    <p className="text-[11px] text-slate-400 mb-1.5">4–6 digits. The tablet asks for this once to pair, keeping the kiosk locked. Leave blank to skip.</p>
                    <input
                      data-testid="kiosk-activate-pin-input"
                      value={pin}
                      onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setPinErr(""); }}
                      inputMode="numeric"
                      placeholder="e.g. 1234"
                      className="w-40 h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-sm tracking-widest"
                    />
                    {pinErr && <p data-testid="kiosk-activate-pin-error" className="text-xs text-rose-600 mt-1">{pinErr}</p>}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    data-testid="kiosk-activate-confirm"
                    onClick={confirmActivate}
                    disabled={toggling || (data.product_count || 0) === 0}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-3 h-9 text-sm font-semibold"
                  ><Rocket className="h-4 w-4" /> {toggling ? "Activating…" : "Confirm & go live"}</button>
                  <button data-testid="kiosk-activate-cancel" onClick={() => { setActivatePrompt(false); setPin(""); setPinErr(""); }} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 h-9 text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <AlertDialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
            <AlertDialogContent data-testid="kiosk-panel-deactivate-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Deactivate {data.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This takes the kiosk offline and returns it to Draft. Customers won’t be able to use it until you activate it again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="kiosk-panel-deactivate-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="kiosk-panel-deactivate-confirm" onClick={deactivate} className="bg-rose-600 hover:bg-rose-700">Deactivate</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`kiosk-panel-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>

          {/* Overview */}
          {tab === "overview" && (
            <div className="space-y-5" data-testid="kiosk-panel-overview">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <PeriodCard label="Current week" data={data.stats?.current_week} highlight />
                <PeriodCard label="Previous week" data={data.stats?.previous_week} />
                <PeriodCard label="Month to date" data={data.stats?.mtd} />
                <PeriodCard label="Year to date" data={data.stats?.ytd} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Fact icon={Package} label="Products" value={data.product_count} />
                <Fact icon={AlertTriangle} label="Low stock" value={data.low_stock} tone={data.low_stock ? "rose" : "slate"} />
                <Fact icon={ShoppingBag} label="Total sales" value={data.transaction_count} />
                <Fact icon={TrendingUp} label="All-time rev." value={money(data.total_revenue)} tone="emerald" />
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-1.5"><Bell className="h-4 w-4 text-indigo-600" /> Notifications</h3>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 divide-y divide-slate-100 dark:divide-slate-800" data-testid="kiosk-panel-notif-events">
                  <p className="px-4 pt-3 pb-1 text-xs text-slate-400">Control which events this kiosk generates. Each admin still chooses whether they receive them in their profile.</p>
                  {[
                    { key: "purchase", label: "Every purchase" },
                    { key: "daily_summary", label: "Daily sales summary" },
                    { key: "low_stock", label: "Low stock digest" },
                  ].map((row) => (
                    <div key={row.key} className="flex items-center justify-between px-4 py-2.5" data-testid={`kiosk-notif-${row.key}`}>
                      <span className="text-sm text-slate-700 dark:text-slate-200">{row.label}</span>
                      <Switch data-testid={`kiosk-notif-${row.key}-switch`} checked={data.notif_events?.[row.key] !== false} onCheckedChange={(v) => toggleNotifEvent(row.key, v)} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-1.5"><UserCog className="h-4 w-4 text-indigo-600" /> Assigned managers</h3>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden" data-testid="kiosk-panel-managers">
                  <p className="px-4 pt-3 pb-2 text-xs text-slate-400">Kiosk managers who cover this kiosk via its building{data.building_name ? ` (${data.building_name})` : ""}. They always receive its offline alerts.</p>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.managers?.length ? data.managers.map((m) => (
                      <div key={m.email} data-testid={`kiosk-manager-${m.email}`} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="h-7 w-7 rounded-full bg-indigo-100 text-indigo-600 grid place-items-center shrink-0"><Users className="h-3.5 w-3.5" /></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{m.name}</p>
                          <p className="text-xs text-slate-400 truncate">{m.email}</p>
                        </div>
                        {m.invited && <span className="text-[10px] font-semibold uppercase rounded-full bg-amber-100 text-amber-700 px-2 py-0.5">Invite pending</span>}
                      </div>
                    )) : (
                      <p className="px-4 py-3 text-sm text-slate-400">No kiosk managers assigned to this building yet.</p>
                    )}
                  </div>
                  <Link to="/admin/users" data-testid="kiosk-panel-manage-users" className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 dark:border-slate-800 text-sm font-medium text-indigo-600 hover:bg-indigo-50/60 dark:hover:bg-slate-900">
                    <span>Assign managers on the Users page</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Recent activity</h3>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {data.activity?.length ? data.activity.map((a, i) => {
                    const Icon = ACT_ICON[a.type] || ActivityIcon;
                    return (
                      <div key={i} data-testid={`kiosk-panel-activity-${i}`} className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-950">
                        <div className={`h-7 w-7 rounded-full grid place-items-center shrink-0 ${a.type === "sale" ? "bg-emerald-100 text-emerald-600" : a.type === "stock" ? "bg-blue-100 text-blue-600" : "bg-indigo-100 text-indigo-600"}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{a.text}</p>
                          <p className="text-xs text-slate-400">{fmtDateTime(a.at)}{a.who ? ` · ${a.who}` : ""}</p>
                        </div>
                        {a.amount != null && <span className="text-sm font-semibold text-emerald-600">{money(a.amount)}</span>}
                      </div>
                    );
                  }) : <p className="px-4 py-3 text-sm text-slate-400 bg-white dark:bg-slate-950">No activity yet.</p>}
                </div>
              </div>
            </div>
          )}

          {/* Transactions */}
          {tab === "transactions" && (
            <div data-testid="kiosk-panel-transactions">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                {data.recent_transactions?.length ? data.recent_transactions.map((t) => (
                  <div key={t.transaction_id} data-testid={`kiosk-panel-txn-${t.transaction_id}`} className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-950">
                    <ShoppingBag className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{t.email || "Guest"} · {t.item_count} item(s)</p>
                      <p className="text-xs text-slate-400">{fmtDateTime(t.created_at)}{t.payment_status !== "paid" ? ` · ${t.payment_status}` : ""}</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{money(t.gross)}</span>
                  </div>
                )) : <p className="px-4 py-3 text-sm text-slate-400 bg-white dark:bg-slate-950">No sales yet.</p>}
              </div>
            </div>
          )}

          {/* Products */}
          {tab === "products" && (
            <div className="space-y-3" data-testid="kiosk-panel-products">
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300 leading-relaxed" data-testid="products-help">
                <p className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Live stock on hand</p>
                <p className="mt-1">The number below is how many units are <b>physically in the kiosk right now</b>. It falls as customers buy and you top it up with − / +. A <b className="text-rose-600">LOW</b> flag means it's at or below the product's PAR. This is different from the <b>Planogram</b> tab, which sets the full-stock shelf capacity used for restock lists.</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="relative flex-1">
                  <input
                    data-testid="kiosk-panel-add-search"
                    value={addQuery}
                    onChange={(e) => { setAddQuery(e.target.value); setAddPid(""); setAddOpen(true); }}
                    onFocus={() => setAddOpen(true)}
                    onBlur={() => setTimeout(() => setAddOpen(false), 150)}
                    placeholder="Search a product to add…"
                    className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-sm"
                  />
                  {addOpen && (() => {
                    const q = addQuery.trim().toLowerCase();
                    const matches = allProducts
                      .filter((p) => !p.deleted && !p.archived && !(data.products || []).some((x) => x.product_id === p.id))
                      .filter((p) => !q || `${p.name} ${p.sku || ""}`.toLowerCase().includes(q))
                      .slice(0, 50);
                    return (
                      <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 shadow-lg" data-testid="kiosk-panel-add-results">
                        {matches.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`kiosk-panel-add-opt-${p.sku || p.id}`}
                            onMouseDown={(e) => { e.preventDefault(); setAddPid(p.id); setAddQuery(`${p.name}${p.sku ? ` (${p.sku})` : ""}`); setAddOpen(false); }}
                            className={`block w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 ${addPid === p.id ? "bg-indigo-50 dark:bg-indigo-950" : ""}`}
                          >
                            {p.name}{p.sku ? <span className="text-slate-400"> ({p.sku})</span> : null}
                          </button>
                        ))}
                        {!matches.length && <p className="px-3 py-2 text-sm text-slate-400">No matching products.</p>}
                      </div>
                    );
                  })()}
                </div>
                <button
                  data-testid="kiosk-panel-add-product"
                  onClick={addProduct}
                  disabled={!addPid || mutating}
                  className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white px-3 h-9 text-sm font-semibold shrink-0"
                ><Plus className="h-4 w-4" /> Add</button>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                {data.products?.length ? data.products.map((p) => (
                  <div key={p.product_id} data-testid={`kiosk-panel-product-${p.sku || p.product_id}`} className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-950">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{p.name}</p>
                      <p className="text-xs text-slate-400">{p.sku || "—"} · {money(p.price_incl)} · PAR {p.par_level}</p>
                    </div>
                    {p.low && <span className="text-[10px] font-bold uppercase text-rose-500">Low</span>}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        data-testid={`kiosk-panel-stock-dec-${p.sku || p.product_id}`}
                        disabled={adjusting === p.product_id || p.stock_qty <= 0}
                        onClick={() => adjust(p, -1)}
                        className="h-7 w-7 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span data-testid={`kiosk-panel-stock-qty-${p.sku || p.product_id}`} className={`w-10 text-center text-sm font-bold tabular-nums ${p.low ? "text-rose-600" : "text-slate-900 dark:text-white"}`}>{p.stock_qty}</span>
                      <button
                        data-testid={`kiosk-panel-stock-inc-${p.sku || p.product_id}`}
                        disabled={adjusting === p.product_id}
                        onClick={() => adjust(p, 1)}
                        className="h-7 w-7 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button
                      data-testid={`kiosk-panel-remove-${p.sku || p.product_id}`}
                      onClick={() => removeProduct(p)}
                      disabled={mutating}
                      title="Remove from kiosk"
                      className="h-7 w-7 rounded-md grid place-items-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 shrink-0 disabled:opacity-40"
                    ><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )) : <p className="px-4 py-3 text-sm text-slate-400 bg-white dark:bg-slate-950">No products assigned.</p>}
              </div>
            </div>
          )}
          {tab === "planogram" && (
            <div data-testid="kiosk-panel-planogram">
              <PlanogramEditor kioskId={data.id} />
            </div>
          )}
          {tab === "health" && <KioskHealth kioskId={data.id} />}
          <div className="pt-4 mt-2 border-t border-rose-100 dark:border-rose-950/40">
            {confirming ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-950/20 p-4 space-y-3" data-testid="kiosk-retire-confirm-box">
                <p className="text-sm text-rose-700 dark:text-rose-300 font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Retire this kiosk permanently?</p>
                <p className="text-xs text-rose-600/80 dark:text-rose-300/70">This removes it from Sites &amp; Kiosks and deletes its stock assignments. Sales history is kept. Type <b>{data.code}</b> to confirm.</p>
                <input
                  data-testid="kiosk-retire-input"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={data.code}
                  className="w-full rounded-lg border border-rose-300 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                />
                <div className="flex items-center gap-2">
                  <button data-testid="kiosk-retire-cancel" onClick={() => { setConfirming(false); setTyped(""); }} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm">Cancel</button>
                  <button
                    data-testid="kiosk-retire-confirm"
                    disabled={typed !== data.code || deleting}
                    onClick={doDelete}
                    className="rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5"
                  ><Trash2 className="h-3.5 w-3.5" /> {deleting ? "Retiring…" : "Retire kiosk"}</button>
                </div>
              </div>
            ) : (
              <button data-testid="kiosk-retire-open" onClick={() => setConfirming(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-700">
                <Trash2 className="h-4 w-4" /> Retire kiosk
              </button>
            )}
          </div>
        </div>
      )}
    </DetailPanel>
  );
}
